import { defineStore } from 'pinia';
import { io } from 'socket.io-client';
import { ref } from 'vue'

import mitt from 'mitt';

import chalk from 'chalk';

import {useLoadingStore} from '@/stores/loadingStore.js';

const api_url = process.env.API_URL;

// ЕИС отвечает на неверный логин/пароль интеграции нарушением вида
// "Ошибка проверки аутентификационной информации; ...InvalidUserException; ERROR; <описание>\n<stacktrace>"
const EIS_AUTH_ERROR_MARKERS = ['InvalidUserException', 'Ошибка проверки аутентификационной информации'];

function findEisAuthErrorText(data) {
    const violations = Array.isArray(data?.violations) ? data.violations : [data?.violations];
    return [...violations, data?.error, data?.message].find((text) =>
        typeof text === 'string' && EIS_AUTH_ERROR_MARKERS.some((marker) => text.includes(marker))
    );
}

// парсеры платежей (mars_parsers) отвечают этой строкой в поле error, если такое п/п уже сохранено
const PAYMENT_ALREADY_EXISTS_ERROR = 'Платежное поручение уже есть в базе данных!';

function makeEisAuthErrorMessage(authErrorText) {
    // описание от ЕИС — четвёртое поле первой строки, без кавычек и стектрейса
    const description = authErrorText.split('\n')[0].replace(/^"|"$/g, '').split('; ').slice(3).join('; ');
    return 'ЕИС не принял логин или пароль интеграции. Проверьте данные в разделе «Профиль» → «Интеграция с ЕИС».'
        + (description ? `\nОтвет ЕИС: ${description}` : '');
}

export const useSocketStore = defineStore('socketio', {
    state: () => ({
        host: `${api_url}`,
        authSocket: null,
        unauthSocket: null,
        isConnected: false,
        socketEvents: mitt(),
        success: ref(new Map()),
        error: ref(new Map()),
        warning: ref(new Map()),
        pending: ref(new Map()),
    }),
    actions: {

        unauthConnect() {
            this.unauthSocket = io(`${this.host}/unauth`, {
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000, // milliseconds
                reconnectionDelayMax: 5000, // milliseconds
                transports: ['websocket', 'polling']
            });

            this.unauthSocket.onAny((eventName, ...args) => {
                console.log(`SocketIO ${chalk.cyan('Response')}: ${eventName}`);
                args.forEach((arg) =>
                    console.log(arg)
                )
            });

            const emit = this.unauthSocket.emit;
            let sock = this.unauthSocket;
            this.unauthSocket.emit = function (eventName, ...args) {
                console.log(`SocketIO ${chalk.green('Request')}: ${eventName}`);
                args.forEach((arg) =>
                    console.log(arg)
                )
                emit.call(sock, eventName, ...args);
            };

            this.unauthSocket.on('emailServiceReceived', (data) => {
                this.socketEvents.emit('emailServiceReceived', data);
            });

            this.unauthSocket.on('emailServiceResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                this.socketEvents.emit('emailServiceResponse', data);
            });
        },
        connect(token) {
            this.authSocket = io(`${this.host}/auth`, {
                extraHeaders: {
                    Authorization: `Bearer ${token}`,
                },
                auth: {
                    token: token
                },
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000, // milliseconds
                reconnectionDelayMax: 5000, // milliseconds
                transports: ['websocket', 'polling']
            });

            this.authSocket.onAny((eventName, ...args) => {
                console.log(`SocketIO ${chalk.cyan('Response')}: ${eventName}`);
                args.forEach((arg) =>
                    console.log(arg)
                )
            });

            const emit = this.authSocket.emit;
            let sock = this.authSocket;
            this.authSocket.emit = function (eventName, ...args) {
                console.log(`SocketIO ${chalk.green('Request')}: ${eventName}`);
                args.forEach((arg) =>
                    console.log(arg)
                )
                emit.call(sock, eventName, ...args);
            };

            this.authSocket.on('connect', () => {
                this.isConnected = true;
            });

            this.authSocket.on('disconnect', () => {
                this.isConnected = false;
            });

            this.authSocket.on('readyDocumentResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                this.socketEvents.emit('readyDocumentResponse', data);
            });

            this.authSocket.on('readyDocumentReceived', (data) => {
                this.socketEvents.emit('readyDocumentReceived', data);
            });

            this.authSocket.on('eisPlanResponse', (data) => {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }

                if (data.error) {
                    this.handleSocketError(data, data.key || JSON.stringify(data), data.message, data.error)
                } else {
                    this.handleSocketSuccess(data, data.key || JSON.stringify(data), data.message)
                }

                this.socketEvents.emit('eisPlanResponse', data);
            });

            this.authSocket.on('notificationMapInvoiceItemsResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                if (data.error) {
                    this.handleSocketError(data, data.upload_file_name, `Не удалось обработать УПД "${data.upload_file_name}"`, data.error)
                } else {
                    this.handleSocketSuccess(data, data.upload_file_name, "УПД обработано", `УПД "${data.upload_file_name}" успешно обработано`)
                }
                console.log('notificationMapInvoiceItemsResponse');

                this.socketEvents.emit('notificationMapInvoiceItemsResponse', data);
            });
            this.authSocket.on('notificationEisContractsResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);
                let isEisContractsByPeriod = data.date_from
                const eisAuthErrorText = data.error ? findEisAuthErrorText(data) : undefined
                if (eisAuthErrorText) {
                    this.handleSocketError(data, isEisContractsByPeriod || data.eais_registry_number, "Ошибка настроек интеграции с ЕИС", makeEisAuthErrorMessage(eisAuthErrorText))
                } else if (isEisContractsByPeriod) {
                    // за период
                    if (data.error) {
                        this.handleSocketError(data, isEisContractsByPeriod, "Ошибка обработки договоров", data.error)
                    } else {
                        this.handleSocketSuccess(data, isEisContractsByPeriod, "Договоры успешно обработаны", `Договоры за период ${data.date_from}-${data.date_to} успешно обработаны и добавлены`)
                    }
                } else {
                    // один договор
                    if (data.error) {
                        this.handleSocketError(data, data.eais_registry_number, data.error, Array.isArray(data.violations) ? data.violations.join('\n') : data.violations)
                    } else {
                        this.handleSocketSuccess(data, data.eais_registry_number, "Договор успешно обработан", `Номер загруженного договора: ${data.eais_registry_number} `)
                    }
                }

                console.log('notificationEisContractsResponse');

                this.socketEvents.emit('notificationEisContractsResponse', data);
            });
            this.authSocket.on('notificationImportPaymentsResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);
                if (data.error === PAYMENT_ALREADY_EXISTS_ERROR) {
                    this.handleSocketWarning(data, data.upload_file_name, `Платежное поручение "${data.upload_file_name}" уже загружено`, data.message || 'Это платежное поручение уже есть в МАРС, повторная загрузка не требуется')
                } else if (data.error) {
                    this.handleSocketError(data, data.upload_file_name, `Не удалось обработать платежное поручение "${data.upload_file_name}"`, data.message || (typeof data.error === 'string' ? data.error : undefined))
                } else {
                    this.handleSocketSuccess(data, data.upload_file_name, "Платежное поручение обработано", data.message)
                }
                this.socketEvents.emit('notificationImportPaymentsResponse', data);
            });
            this.authSocket.on('notificationPerfomanceContractResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                const registration_number = this.pending.get(data.performance_contract_guid)?.registration_number

                if (data.result === 'success') {
                    this.handleSocketSuccess(data, data.performance_contract_guid, "Исполнение обработано", `Исполнение для договора "${registration_number}" успешно обработано`)
                } else {
                    this.handleSocketError(data, data.performance_contract_guid, `Не удалось обработать исполнение для договора "${registration_number}"`, data.error)
                }
                this.socketEvents.emit('notificationPerfomanceContractResponse', data);
            });
            this.authSocket.on('reportCreateResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);
                if (data.error) {
                    this.handleSocketError(data, `${data.contract_guid}${data.report_code}`, `Не удалось создать отчет для договора "${data.contract_regnumber}"`, data.error)
                } else {
                    this.handleSocketSuccess(data, `${data.contract_guid}${data.report_code}`, "Отчет успешно сформирован", `Excel файл успешно сформирован`)
                }
                this.socketEvents.emit('reportCreateResponse', data);
            });
            this.authSocket.on('catalogUserfieldsResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                this.socketEvents.emit('catalogUserfieldsResponse', data);
            });

            this.authSocket.on('documentUserfieldsReceived', (data) => {

            });

            this.authSocket.on('catalogUserfieldsReceived', (data) => {

            });

            this.authSocket.on('copyProcurementRuleReceived', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                this.socketEvents.emit('copyProcurementRuleReceived', data);
            });

            this.authSocket.on('copyProcurementRuleResponse', (data) => {
                const loadingStore = useLoadingStore();
                loadingStore.setLoading(false);

                this.socketEvents.emit('copyProcurementRuleResponse', data);
            });
            this.authSocket.on('sendEISPerformanceContract', (data) => {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }

                if (!data?.header || !data?.message) {
                    return;
                }

                if (data.error) {
                    this.handleSocketError(data, data.key || JSON.stringify(data), data.header, data.message);
                } else {
                    this.handleSocketSuccess(data, data.key || JSON.stringify(data), data.header, data.message);
                }
            })
        },
        disconnect() {
            this.authSocket.disconnect();
            this.authSocket = null;
            this.isConnected = false;
        },
        handleSocketError(data, key, header, message, sentry = false) {
            if (!header && !message && !data?.header && !data?.message) return;
            if (!key) key = Math.floor(Math.random() * 1000000) + 1;
            if (header) data.header = header;
            if (message) data.message = message;
            if (sentry) data.sentry = sentry;
            this.pending.delete(`${key}`)
            this.error.set(`${key}`, data);
            setTimeout(() => {
                if (this.error.has(`${key}`)) {
                    this.error.delete(`${key}`);
                }
            }, 10000);
        },
        handleSocketSuccess(data, key, header, message) {
            const resolvedHeader = header || data?.header;
            const resolvedMessage = message || data?.message;
            if (!resolvedHeader && !resolvedMessage) return;

            if (resolvedHeader) data.header = resolvedHeader;
            if (resolvedMessage) data.message = resolvedMessage;

            if (!key) key = Math.floor(Math.random() * 1000000) + 1;
            this.pending.delete(`${key}`)
            this.success.set(`${key}`, data);
            setTimeout(() => {
                if (this.success.has(`${key}`)) {
                    this.success.delete(`${key}`);
                }
            }, 10000);
        },
        handleSocketWarning(data, key, header, message) {
            if (header) data.header = header;
            if (message) data.message = message;
            if (!key) key = Math.floor(Math.random() * 1000000) + 1;
            this.pending.delete(`${key}`)
            this.warning.set(`${key}`, data);
            setTimeout(() => {
                if (this.warning.has(`${key}`)) {
                    this.warning.delete(`${key}`);
                }
            }, 10000);
        },
        handleSocketPending(data, key) {
            this.pending.set(`${key}`, data);
        },
        sendReadyDocumentRequest(message) {
            const loadingStore = useLoadingStore();
            loadingStore.setLoading(true);
            loadingStore.setLoadingMessage('socketReadyDocumentRequest');

            this.authSocket.emit('readyDocumentRequest', message);
        },
        sendDocumentUserfieldsRequest(message) {
            const loadingStore = useLoadingStore();
            loadingStore.setLoading(true);
            loadingStore.setLoadingMessage('socketDocumentUserfieldsRequest');

            this.authSocket.emit('documentUserfieldsRequest', message);
        },
        sendEmailRequest(message) {
            const loadingStore = useLoadingStore();
            loadingStore.setLoading(true);
            loadingStore.setLoadingMessage('sendEmailRequest');

            this.unauthSocket.emit('emailServiceRequest', message);
        },
        sendCopyProcurementRule(message) {
            const loadingStore = useLoadingStore();
            loadingStore.setLoading(true);
            loadingStore.setLoadingMessage('socketCopyProcurementRule');

            this.authSocket.emit('copyProcurementRuleRequest', message);
        }
    },
});
